/*
  File: src/App.js
  Purpose: Application shell and routing.
*/
import React, { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import HomePage from "./pages/HomePage";
import LandingPage from "./pages/LandingPage";
import TvPage from "./pages/TvPage";
import StorePage from "./pages/StorePage";
import ProductDetail from "./pages/ProductDetail";
import Dashboard from "./pages/Dashboard";
import CartPage from "./pages/CartPage";
import ProfilePage from "./pages/ProfilePage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import WatchPage from "./pages/WatchPage";
import SeriesWatchPage from "./pages/SeriesWatchPage";
import DetectionsHistory from "./pages/DetectionsHistory";
import ProductManagement from "./pages/ProductManagement";
import ProductUpdate from "./pages/ProductUpdate";
import CharactersManagement from "./pages/CharactersManagement";
import PlayersManagement from "./pages/PlayersManagement";
import VendorsManagement from "./pages/VendorsManagement";
import CategoriesManagement from "./pages/CategoriesManagement";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { CartProvider } from "./cart/CartContext";
import OrdersPage from "./pages/OrdersPage";
import RekognitionTest from "./pages/RekognitionTest";
import RekognitionManagement from "./pages/RekognitionManagement";
import DatasetDetail from "./pages/DatasetDetail";
import Snaps from "./pages/Snaps";
import NotFound from "./pages/NotFound";
import FootballMemoryGame from "./pages/FootballMemoryGame";
import ManagementHub from "./pages/ManagementHub";
import ProductResizing from "./pages/ProductResizing";
import Modal from "./components/Modal";
import PrivacyPolicyContent from "./components/PrivacyPolicyContent";
import TermsOfServiceContent from "./components/TermsOfServiceContent";
import SupportContent from "./components/SupportContent";
import { ModalProvider } from "./contexts/ModalContext";

// Dev-only pages visibility flag: enable via REACT_APP_SHOW_DEV_PAGES=true
// Defaults to true in non-production builds
const SHOW_DEV_PAGES = (() => {
  const envVal = String(process.env.REACT_APP_SHOW_DEV_PAGES || '').trim().toLowerCase();
  if (envVal === 'true') return true;
  if (envVal === 'false') return false;
  return process.env.NODE_ENV !== 'production';
})();

function ProtectedRoute({ children }) {
  const { isAuthenticated, authChecked } = useAuth();
  if (!authChecked) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalContent, setModalContent] = useState(null);

  const openModal = (title, content) => {
    setModalTitle(title);
    setModalContent(content);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalTitle("");
    setModalContent(null);
  };

  return (
    <AuthProvider>
      <CartProvider>
        <ModalProvider openModal={openModal}>
          <BrowserRouter>
            <div className="w-full">
              <Routes>
                <Route path="/" element={<ProtectedRoute><LandingPage /></ProtectedRoute>} />
                <Route path="/football" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
                <Route path="/football-memory" element={<ProtectedRoute><FootballMemoryGame /></ProtectedRoute>} />
                <Route path="/tv" element={<ProtectedRoute><TvPage /></ProtectedRoute>} />
                <Route path="/watch" element={<ProtectedRoute><WatchPage /></ProtectedRoute>} />
                <Route path="/series/watch" element={<ProtectedRoute><SeriesWatchPage /></ProtectedRoute>} />
                <Route path="/store/:id" element={<ProtectedRoute><ProductDetail /></ProtectedRoute>} />
                <Route path="/store" element={<ProtectedRoute><StorePage /></ProtectedRoute>} />
                <Route path="/cart" element={<ProtectedRoute><CartPage /></ProtectedRoute>} />
                <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
                <Route path="/dashboard/*" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                {SHOW_DEV_PAGES && (
                  <Route path="/history" element={<ProtectedRoute><DetectionsHistory /></ProtectedRoute>} />
                )}
                {SHOW_DEV_PAGES && (
                  <Route path="/rekognition-test" element={<ProtectedRoute><RekognitionTest /></ProtectedRoute>} />
                )}
                {SHOW_DEV_PAGES && (
                  <Route path="/snaps" element={<ProtectedRoute><Snaps /></ProtectedRoute>} />
                )}
                {SHOW_DEV_PAGES && (
                  <>
                    <Route path="/productmanagement" element={<ProtectedRoute><ProductManagement /></ProtectedRoute>} />
                    <Route path="/characters" element={<ProtectedRoute><CharactersManagement /></ProtectedRoute>} />
                    <Route path="/players" element={<ProtectedRoute><PlayersManagement /></ProtectedRoute>} />
                    <Route path="/vendors" element={<ProtectedRoute><VendorsManagement /></ProtectedRoute>} />
                    <Route path="/categories" element={<ProtectedRoute><CategoriesManagement /></ProtectedRoute>} />
                    <Route path="/rekognition-management" element={<ProtectedRoute><RekognitionManagement /></ProtectedRoute>} />
                    <Route path="/rekognition-management/:id" element={<ProtectedRoute><DatasetDetail /></ProtectedRoute>} />
                    <Route path="/management" element={<ProtectedRoute><ManagementHub /></ProtectedRoute>} />
                    <Route path="/product-resizing" element={<ProtectedRoute><ProductResizing /></ProtectedRoute>} />
                    <Route path="/productupdate" element={<ProtectedRoute><ProductUpdate /></ProtectedRoute>} />
                  </>
                )}
                <Route path="/app" element={<ProtectedRoute><WatchPage /></ProtectedRoute>} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/signup" element={<SignupPage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </div>
            <Modal show={showModal} title={modalTitle} onClose={closeModal}>
              {modalContent}
            </Modal>
          </BrowserRouter>
        </ModalProvider>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
